import { all, fork, put, takeLatest } from "redux-saga/effects";
import { getQuery, postQuery, putQuery } from "utils/api";
import { Toast } from "utils/toast";
import { clearAuthTokens } from "utils/tokenUtils";
import { takePostLoginPath } from "utils/postLogin";
import { actions } from "./action";
import { actions as loaderAction } from "redux/Loader/action";
import { actions as brandAction } from "redux/Brand/action";


export function* userRegistration() {
  yield takeLatest(
    actions.USER_REGISTRATION_REQUEST,
    function* ({ data, navigate }) {
      try {
        const response = yield postQuery(`registration`, data);

        if (!response.data.error) {
          yield put({
            type: actions.USER_REGISTRATION_SUCCESS,
            payload: response.data.result,
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  );
}

export function* userLogin() {
  yield takeLatest(actions.USER_LOGIN_REQUEST, function* ({ data, navigate }) {
    try {
      // A previous account's tokens must not ride along or outlive this login.
      clearAuthTokens();
      const response = yield postQuery(`tutor/login`, {
        ...data,
        password: data.password,
      });
      // const response = yield postQuery(`tutor/login`, {...data});
      if (
        !response.data.error &&
        (response.data.result?.mfaRequired || response.data.result?.mfaSetupRequired)
      ) {
        // No session yet: hand over to the code / setup step.
        yield put({
          type: actions.USER_MFA_PENDING,
          payload: {
            mfaRequired: !!response.data.result.mfaRequired,
            mfaSetupRequired: !!response.data.result.mfaSetupRequired,
            mfaToken: response.data.result.mfaToken,
          },
        });
        yield put({ type: actions.CATCH_ERROR, payload: "" });
        return;
      }
      if (!response.data.result?.token || !response.data.result?.refreshToken) {
        yield put({
          type: actions.CATCH_ERROR,
          payload: "Something went wrong. Please try again.",
        });
        return;
      }
      if (!response.data.error) {
        yield put({
          type: actions.USER_LOGIN_SUCCESS,
          payload: response.data.result,
        });
        localStorage.setItem("TOKEN", response.data.result.token);
        localStorage.setItem(
          "REFRESH_TOKEN",
          response.data.result.refreshToken
        );

        yield put({
          type: actions.CATCH_ERROR,
          payload: "",
        });
        navigate(takePostLoginPath());
      }
    } catch (e) {
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("REFRESH_TOKEN");
      console.error(e);
      yield put({
        type: actions.CATCH_ERROR,
        payload: e.response.data.message,
      });
    }
  });
}

export function* userRefreshToken() {
  yield takeLatest(
    actions.USER_REFRESH_TOKEN_REQUEST,
    function* ({ data, navigate }) {
      try {
        const header = {
          headers: {
            authorization: `${data}`,
          },
        };

        const response = yield postQuery(`tutor/refresh-token`, {}, header);

        if (!response.data.error) {
          yield put({
            type: actions.USER_REFRESH_TOKEN_SUCCESS,
            payload: response.data.result,
          });
        }
        localStorage.setItem("TOKEN", response.data.result.token);
        localStorage.setItem(
          "REFRESH_TOKEN",
          response.data.result.refreshToken
        );

        yield put({
          type: actions.CATCH_ERROR,
          payload: "",
        });
        navigate("/");
      } catch (e) {
        localStorage.removeItem("TOKEN");
        localStorage.removeItem("REFRESH_TOKEN");
        console.error(e);
        navigate("/auth/login");
      }
    }
  );
}

export function* userDetail() {
  yield takeLatest(actions.USER_DETAIL_REQUEST, function* ({ data, navigate }) {
    try {
      const response = yield getQuery(
        data === "tutor" ? `gettutor` : "getadmin"
      );
      if (!response.data.error) {
        yield put({
          type: actions.USER_DETAIL_SUCCESS,
          payload: response.data.result,
        });
        yield put({
          type: brandAction.GET_BRAND_THEME_SUCCESS,
          payload: response.data.result.brand,

        });
        if (
          response.data.result?.name === "" ||
          response.data.result?.designation === "" ||
          response.data.result?.email === ""
        ) {
          navigate("/profile");
          Toast("info", "Please Add Your Profile Details!");
        }
      }
    } catch (e) {
      console.error(e);
      const status = e.response?.status;
      if (status === 401) {
        localStorage.removeItem("TOKEN");
        localStorage.removeItem("REFRESH_TOKEN");
        navigate("/auth/login");
      }
    }
  });
}

export function* userUpdate() {
  yield takeLatest(actions.USER_UPDATE_REQUEST, function* ({ data, endpoint }) {
    try {
      const response = yield putQuery(endpoint, data);
      if (!response.data.error) {
        yield put({
          type: actions.USER_UPDATE_SUCCESS,
          payload: response.data.result,
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        Toast("success", "Profile updated successfully");
      } else {
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
      }
    } catch (e) {
      console.error(e);
      yield put({
        type: loaderAction.CLOSE_LOADER,
      });
    }
  });
}

export function* updatePassword() {
  yield takeLatest(
    actions.USER_UPDATE_PASSWORD_REQUEST,
    function* ({ data, navigate }) {
      try {
        const response = yield putQuery("tutor/password", data);
        if (!response.data.error) {
          yield put({
            type: actions.USER_UPDATE_PASSWORD_SUCCESS,
            payload: response.data.result,
          });
          Toast("success", "Password updated successfully");
          yield put({
            type: actions.USER_LOGOUT_SUCCESS,
          });
          localStorage.removeItem("TOKEN");
          yield put({
            type: actions.CATCH_ERROR,
            payload: "",
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
          navigate("/auth/login");
        }
      } catch (e) {
        console.error(e);
        yield put({
          type: actions.CATCH_ERROR,
          payload: e.response.data.message,
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
      }
    }
  );
}

export function* userLogout() {
  yield takeLatest(actions.USER_LOGOUT_REQUEST, function* ({ navigate }) {
    try {
      // Ends the session on the server, so this token stops working.
      yield getQuery("tutor/logout");
    } catch (e) {
      console.error(e);
    } finally {
      // Signed out locally whether or not the server call succeeded.
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("REFRESH_TOKEN");
      yield put({
        type: actions.USER_LOGOUT_SUCCESS,
      });
      navigate("/auth/login");
    }
  });
}

export default function* userSaga() {
  yield all([
    fork(userLogin),
    fork(userRegistration),
    fork(userLogout),
    fork(userDetail),
    fork(userUpdate),
    fork(updatePassword),
    fork(userRefreshToken),
  ]);
}
